import React from 'react';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 } from 'uuid';
import MetricResult, { MetricResultType } from '../types/metrics/metricResult';
import { DashboardContext, DashboardContextProps } from '../context/DashboardContext';

const dashboardState = {
  items: new Map<string, { data: MetricResult[]; type: MetricResultType }>(),
  loading: false,
};

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: { key: v4() },
  reducers: {
    reset: state => {
      dashboardState.items = new Map();
      dashboardState.loading = true;
      state.key = v4();
    },
    set: (state, action: PayloadAction<{ key: string; data: { data: MetricResult[]; type: MetricResultType } }[]>) => {
      dashboardState.items = new Map();
      action.payload.forEach(p => dashboardState.items.set(p.key, p.data));
      state.key = v4();
    },
    add: (state, action: PayloadAction<{ key: string; data: { data: MetricResult[]; type: MetricResultType } }[]>) => {
      action.payload.forEach(p => dashboardState.items.set(p.key, p.data));
      dashboardState.items = new Map(dashboardState.items);
      state.key = v4();
    },
    delete: (
      state,
      action: PayloadAction<{ key: string; data: { data: MetricResult[]; type: MetricResultType } }[]>,
    ) => {
      action.payload.forEach(p => dashboardState.items.delete(p.key));
      dashboardState.items = new Map(dashboardState.items);
      state.key = v4();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      dashboardState.loading = action.payload;
      state.key = v4();
    },
  },
});

interface DashboardProviderProps {
  children: React.ReactNode;
}

export default function DashboardProvider({ children }: DashboardProviderProps) {
  const dispatch = useAppDispatch();
  const key = useAppSelector(state => state.dashboard.key);

  const binContext: DashboardContextProps = {
    cache: dashboardState.items,
    cacheLoading: dashboardState.loading,
    setCache: (items: Map<string, { data: MetricResult[]; type: MetricResultType }>) => {
      dispatch(
        dashboardSlice.actions.set(
          [...items.entries()].map(([key, data]) => {
            return { key, data };
          }),
        ),
      );
    },
  };

  return <DashboardContext.Provider value={binContext}>{children}</DashboardContext.Provider>;
}
